'use client';

import { Phone } from 'lucide-react';
import { EditableContent } from '../../edit/EditableContent';
import { LucideIcon } from '../../icons/LucideIcon';
import type { BlockRenderProps } from '../../types';
import { ContactMessage, ContactPicture } from './contactChrome';
import { readContactView } from './contactView';

/**
 * Threshold: the photograph is the first screen. Three chips sit on its
 * edge — call, first sitting (with day), nearest landmark. The message overlaps that edge.
 */
export function ContactThreshold({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
}: BlockRenderProps) {
  const view = readContactView({ block, locale, isActive, activeEditTarget, editable });
  const { target } = view;

  return (
    <section
      className={['ws-contact-threshold', isActive ? 'ws-block--active' : ''].filter(Boolean).join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-contact-arrangement="threshold"
      id="visit"
    >
      <div className="ws-contact-threshold__stage">
        {view.showMap ? (
          <ContactPicture
            view={view}
            className="ws-contact-threshold__visual"
            photoClass="ws-contact-threshold__photo"
          />
        ) : (
          <div className="ws-contact-threshold__visual ws-contact-threshold__visual--empty" aria-hidden />
        )}
        <div className="ws-contact-threshold__scrim">
          <EditableContent
            as="h2"
            fieldKey="sectionTitle"
            editable={editable}
            activeEditTarget={target}
            className="ws-contact-threshold__title"
          >
            {view.sectionTitle}
          </EditableContent>
          <ul className="ws-contact-threshold__chips">
            {view.phone || editable ? (
              <li>
                {view.telHref && !editable ? (
                  <a className="ws-contact-threshold__chip" href={view.telHref}>
                    <LucideIcon icon={Phone} size="sm" className="ws-contact__action-icon" />
                    <span>{view.phone}</span>
                  </a>
                ) : (
                  <EditableContent
                    as="span"
                    fieldKey="phone"
                    editable={editable}
                    activeEditTarget={target}
                    className="ws-contact-threshold__chip"
                  >
                    {view.phone}
                  </EditableContent>
                )}
              </li>
            ) : null}
            {view.hoursLine || editable ? (
              <li>
                <EditableContent
                  fieldKey="hours"
                  editable={editable}
                  activeEditTarget={target}
                  className="ws-contact-threshold__chip"
                >
                  {view.hoursLine}
                </EditableContent>
              </li>
            ) : null}
            {view.landmark || editable ? (
              <li>
                <EditableContent
                  fieldKey="directions"
                  editable={editable}
                  activeEditTarget={target}
                  className="ws-contact-threshold__chip"
                >
                  {view.landmark}
                </EditableContent>
              </li>
            ) : null}
          </ul>
        </div>
      </div>

      <div className="ws-contact-threshold__sheet">
        <div className="ws-contact__form-panel ws-contact-threshold__card">
          {view.intro || editable ? (
            <EditableContent
              fieldKey="intro"
              editable={editable}
              activeEditTarget={target}
              className="ws-contact-threshold__intro"
            >
              {view.intro}
            </EditableContent>
          ) : null}
          <ContactMessage
            view={view}
            locale={locale}
            className="ws-contact-threshold__message"
            titleClass="ws-contact__form-title"
            supportClass="ws-contact__form-support"
          />
        </div>
      </div>
    </section>
  );
}
